/**
 * Reverse-mapping utilities: given SVG coordinates from a drag drop,
 * determine which ring band and axis sector the position falls in.
 *
 * Used by AdjacencyMap drag-to-reposition to auto-update
 * relatednessRing and adjacencyAxis when a node is dragged.
 */

import type { AdjacencyAxis, RelatednessRing } from '../types/adjacency';
import { AXIS_ANGLES } from '../types/adjacency';
import { RING_BANDS } from './positioning';

/**
 * Given a normalized radius (0–1 fraction of canvasRadius),
 * return the ring band it falls in.
 */
export function radiusToRing(normalizedRadius: number): RelatednessRing {
  for (const ringStr of Object.keys(RING_BANDS)) {
    const ring = Number(ringStr) as RelatednessRing;
    const band = RING_BANDS[ring];
    if (normalizedRadius >= band.inner && normalizedRadius < band.outer) {
      return ring;
    }
  }
  // Beyond outer edge → diversification
  return 4;
}

/**
 * Given an angle in degrees (clockwise from top / 12 o'clock),
 * return the nearest axis.
 */
export function angleToAxis(cwDegrees: number): AdjacencyAxis {
  // Normalize to 0–360
  const norm = ((cwDegrees % 360) + 360) % 360;
  let closest: AdjacencyAxis = 'New Services';
  let minDiff = 360;
  for (const [axis, angle] of Object.entries(AXIS_ANGLES)) {
    if (angle < 0) continue; // skip Core
    const diff = Math.min(
      Math.abs(norm - angle),
      360 - Math.abs(norm - angle),
    );
    if (diff < minDiff) {
      minDiff = diff;
      closest = axis as AdjacencyAxis;
    }
  }
  return closest;
}
